<template>
    <group-report-template  v-model:current-report-template-id="currentReportTemplateId"
                            :template-id="templateId"
                            v-model:for-group="forGroup"
                            v-model:interval="interval"
                            v-model:ran-report="reportSuccessfullySent"
                            v-model:reload-data="reloadTable"
                            v-model:running-errors="errorSending"
                            v-model:running-report="runningReport"
                            v-model:settings="settings"
                            :can-edit="permissions.can('manage_device_summary_report')"
                            :users="users"
                            :default-settings="defaultSettings"
                            :prepare-settings-function="prepareSettings"
                            :hide-title="true"
                            :use-settings-drawer="true"
                            :use-template-cards="true"
                            :use-group-reporting-option="groupReportingEnabled"
                            :has-settings="hasManualRunSettings"
                            :report-icon="settingsSchema.icon"
                            :schema="settingsSchema"
                            report-name="weekly-device-summary-report"
                            v-model:display-section="displaySection"
                            @useStandardReporting="setUseStandardReporting"
                            @reset-settings="resetSelections"
    >

        <template   #create-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="true"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #edit-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="displaySection === 'show-template' || useStandardReporting"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #run-options="props">
            <!-- Explicit date range over a point selection, matching Excursion /
                 MKT. A saved template's run keeps its interval-derived window.
                 The show-offline toggle is edited in the Report Settings drawer
                 and posted via extraParams. -->
            <interval-selection   v-model:end-of-interval="endOfInterval"
                                        v-model:interval="interval"
                                        :use-range="! props.reportTemplateId"
                                        v-model:range-start="rangeStart"
                                        v-model:range-end="rangeEnd"
                                        v-model:selected-meters="selectedMeters"
                                        :meters="metersByGroup[props.groupId]"
                                        :meter-tree="meterTree"
                                        :footer-mode="props.footerMode ?? 'none'"
                                        :max-date="maxDate"
                                        :min-date="minDate"
                                        :is-running="runningReport"
                                        :ran-report="reportSuccessfullySent"
                                        :running-errors="errorSending"
                                        :timezone="timezone"
                                        @run-report="runReport(props.reportTemplateId ?? null)"
            ></interval-selection>
        </template>
    </group-report-template>
</template>

<script>
import {ref} from 'vue'
import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "../../mixins/ajaxTableMixin";
import {useGroupReport} from "@/composables/useGroupReport";

import GroupReportTemplate from "./GroupReportTemplate";
import IntervalSelection from "./IntervalSelection";
import ReportSettingsPanel from "./settings/ReportSettingsPanel.vue";
import weeklyDeviceSummarySettingsSchema from "./settings/schemas/weekly-device-summary";
import {toReportDate} from "./settings/ReportDateRange.vue";

export default {
    name: "GroupWeeklyDeviceSummaryReport",
    mixins: [SonicuCoreMixin, ajaxTableMixin],
    components: {
        GroupReportTemplate,
        IntervalSelection,
        ReportSettingsPanel,
    },
    props: {
        // null on the report page; 'new' or a group report id on its New or
        // Edit page.
        templateId: {
            type: [String, Number],
            default: null,
        },
        interval: {
            type: String,
            default: 'weekly',
        },
        metersByGroup: {
            type: Object,
            default() {
                return {}
            },
        },
        meterTree: {
            type: Array,
            default() {
                return []
            }
        },
        users: {
            type: Object,
            default() {
                return {}
            }
        },
        minDate: {
            type: [Date, String],
        },
        maxDate: {
            type: [Date, String],
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            }
        },
        timezone: {
            type: String,
            default: 'America/New_York'
        },
    },
    setup(props) {
        // The composable supplies settingsContext, hasManualRunSettings, and
        // prepareSettings (weekday default); this schema has no permission-gated
        // fields. extraParams posts the show-offline toggle the report reads
        // and, on an On-Demand run, the picked range as plain dates
        // (toReportDate). A saved template's run posts no start, so the
        // controller derives the window from the template's interval.
        const rangeStart = ref(null)
        const rangeEnd = ref(null)

        const report = useGroupReport(weeklyDeviceSummarySettingsSchema, {
            interval: props.interval,
            defaultSettings: props.defaultSettings,
            weekdayDefault: 1,
            extraParams: (state, reportTemplateId) => {
                const params = {
                    show_offline: state.settings.value['show_offline'],
                }

                if (reportTemplateId === null) {
                    params.start = toReportDate(rangeStart.value, props.timezone)
                    params.end = toReportDate(rangeEnd.value, props.timezone)
                }

                return params
            },
        })

        return {
            ...report,
            rangeStart,
            rangeEnd,
        }
    },
    methods: {
        resetSelections() {
            this.selectedMeters = []
        },
    },
    computed: {
        settingsSchema() {
            return weeklyDeviceSummarySettingsSchema
        },
    },
}
</script>
